import React, { useState } from 'react';
import { Tooltip, Empty, Modal } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { openNotification } from 'util/helpers';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';
import {
  useGetAssetsByCreatorQuery,
  useFindManyUserSceneAssetsQuery,
} from '@src/components/generated/graphql';
import EcommerceElement from './EcommerceElement';
import AssetCard from './AssetCard';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import DefaultButton from '@src/components/ui-kit/buttons/DefaultButton';
import styles from './created-assets.module.css';

type Props = {
  callback: Function;
};

const notify = () => {
  openNotification(
    'Info',
    "If you can't see recently added assets there, please try to save scene.",
    'top',
    8
  );
};

const CreatedAssets: React.FC<Props> = ({ callback }) => {
  const [openModal, setOpenModal] = useState(false);
  const { data: session } = useSession();
  const router = useRouter();
  const isCheckoutEnabled =
    process.env.NEXT_PUBLIC_ENABLE_CHECKOUT === 'true' ||
    session?.user.role === 'admin';

  const sceneId = Array.isArray(router.query?.id)
    ? router.query?.id[0]
    : router.query?.id;

  const { data: sceneAssets } = useFindManyUserSceneAssetsQuery({
    variables: {
      where: {
        userId: {
          equals: session?.user.id,
        },
        userSceneId: {
          equals: sceneId,
        },
        marketId: {
          not: null,
        },
        type: {
          in: [ASSET_TYPE.GLTF.OTHER, ASSET_TYPE.PRIMITIVE_TEXTURE.IMAGE_PLANE], //Only images and models supported
        },
      },
    },
  });

  const { data, loading } = useGetAssetsByCreatorQuery({
    variables: {
      where: {
        creatorEmail: session?.user.email as string,
        filter: {
          type: 'asset',
          assetName: '',
          collectionName: '',
          categoryKey: '',
        },
      },
    },
  });

  return (
    <div>
      {isCheckoutEnabled && (
        <div className={styles.ecmrce}>
          <DefaultButton
            type='secondary'
            onClick={() => {
              setOpenModal(true);
              notify();
            }}
            style={{ marginBottom: 30 }}
          >
            <span>Enable e-commerce</span>
          </DefaultButton>
          <Tooltip title='You can enable ability to purchase your assets in 3D world for other users.'>
            <QuestionCircleOutlined />
          </Tooltip>
        </div>
      )}
      <h3>Your created Assets:</h3>
      {data?.getAssetsByCreator.assets
        ?.map((el) => {
          return {
            ...el,
            assetURL: el.assetURL.replace(
              'https://ozone-marketplace-assets.s3.amazonaws.com/',
              'https://d3to9p4w6lww5g.cloudfront.net/'
            ),
          };
        })
        .map((el) => {
          return <AssetCard data={el} callback={callback} key={el.id} />;
        })}
      {!data?.getAssetsByCreator.assets && <Empty style={{ marginTop: 20 }} />}
      <Modal
        open={openModal}
        onCancel={() => setOpenModal(false)}
        width={650}
        footer={false}
      >
        <div>
          <div className={styles.ecmrce}>
            <h3>Assets in the scene</h3>
            <Tooltip title='You can choose which asset could be open to purchase. Only images and models are supported now.'>
              <QuestionCircleOutlined />
            </Tooltip>
          </div>
          <div className={styles.assetGrid}>
            {!sceneAssets?.findManyUserSceneAssets.length && <Empty />}
            {sceneAssets?.findManyUserSceneAssets.map((el, i) => {
              return <EcommerceElement key={`${el.id} ${i}`} el={el} />;
            })}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CreatedAssets;
